/**
 * Tenant Configuration for member-web
 *
 * Maps domains to organization IDs and provides fallback config.
 */

export const DEFAULT_ORG_ID = 'kappa';

/**
 * Custom-domain → org map for tenants on their own domain (the two launch tenants
 * plus any premium "custom domain" add-on). Self-serve orgs on `<org>.konklave.app`
 * are resolved by subdomain (see getOrgIdFromHostname) and do NOT need an entry here.
 */
export const DOMAIN_TO_ORG: Record<string, string> = {
  'member.nupeloop.com': 'kappa',
  'members.nupeloop.com': 'kappa',
  'nupeloop.com': 'kappa',
  'www.nupeloop.com': 'kappa',
  // konklave.app / www.konklave.app are the platform MARKETING site (see
  // MARKETING_HOSTS); Kappa is a community at members./kappa.konklave.app.
  'member.konklave.app': 'kappa',
  'members.konklave.app': 'kappa',
  'kappa.konklave.app': 'kappa',
  'crimsonconnect.app': 'dst',
  'www.crimsonconnect.app': 'dst',
  'member.crimsonconnect.app': 'dst',
  'members.crimsonconnect.app': 'dst',
  localhost: 'kappa',
  '127.0.0.1': 'kappa',
};

/**
 * Hosts that should render the platform MARKETING site (Konklave) rather than
 * a tenant member app. Checked before any tenant resolution.
 */
export const MARKETING_HOSTS = new Set<string>(['konklave.app', 'www.konklave.app']);

/**
 * True when the current host should show the marketing landing page.
 * On localhost, set REACT_APP_MARKETING=1 to preview it locally.
 */
export function isMarketingHost(hostname: string = window.location.hostname): boolean {
  if (
    (hostname === 'localhost' || hostname === '127.0.0.1') &&
    process.env.REACT_APP_MARKETING === '1'
  ) {
    return true;
  }
  return MARKETING_HOSTS.has(hostname);
}

/**
 * The platform super-admin console host. The app renders the normal member app
 * here, but the active tenant is chosen via the tenant switcher (not the
 * hostname). `admin` is a reserved subdomain, so no tenant can ever claim it.
 */
export const PLATFORM_ADMIN_HOST = 'admin.konklave.app';
const PLATFORM_ACTIVE_ORG_KEY = 'platform_active_org';

/**
 * Synthetic org id used to brand the platform console as Konklave before a
 * super-admin picks a tenant to manage. The legacy value 'thecrest' is kept
 * because it is stamped on Cognito claims / Firestore docs. Not a real tenant — its config comes
 * from getFallbackConfig() below; API calls for it 404 → fallback.
 */
export const PLATFORM_ORG_SENTINEL = 'thecrest';

/** True on the platform console host (admin.konklave.app). */
export function isPlatformAdminHost(hostname: string = window.location.hostname): boolean {
  if (
    (hostname === 'localhost' || hostname === '127.0.0.1') &&
    process.env.REACT_APP_PLATFORM_ADMIN === '1'
  ) {
    return true;
  }
  return hostname === PLATFORM_ADMIN_HOST;
}

/**
 * The org the app should operate as. On the platform console this is the
 * super-admin's selected tenant (persisted); everywhere else it's derived from
 * the hostname. This is the single source the fetch interceptor + TenantContext
 * read, so a super-admin's switch flows to every API call.
 */
export function getActiveOrgId(): string {
  if (isPlatformAdminHost()) {
    try {
      const v = localStorage.getItem(PLATFORM_ACTIVE_ORG_KEY);
      if (v) return v;
    } catch {
      /* ignore */
    }
    // No tenant chosen yet → brand the console as Konklave (not a tenant).
    return PLATFORM_ORG_SENTINEL;
  }
  return getOrgIdFromHostname();
}

/** Persist the platform console's active tenant. Caller should reload after. */
export function setActiveOrgId(orgId: string): void {
  try {
    localStorage.setItem(PLATFORM_ACTIVE_ORG_KEY, orgId);
  } catch {
    /* ignore */
  }
}

/**
 * For the platform's wildcard host (`<org>.konklave.app`), return the org label
 * so a new self-serve org resolves with no code change. Returns null for the
 * apex/marketing hosts and any non-platform host.
 */
export const PLATFORM_BASE_HOST = 'konklave.app';
const PLATFORM_HOST_SUFFIX = `.${PLATFORM_BASE_HOST}`;
// Subdomains that are never a tenant label (infra / platform hosts).
const NON_TENANT_LABELS = new Set(['www', 'admin', 'api', 'app', 'members', 'member']);
function getPlatformSubdomainOrg(hostname: string): string | null {
  if (!hostname.endsWith(PLATFORM_HOST_SUFFIX)) return null;
  const label = hostname.slice(0, -PLATFORM_HOST_SUFFIX.length);
  // Skip apex/marketing (`www`), reserved hosts and any deeper subdomain.
  if (!label || NON_TENANT_LABELS.has(label) || label.includes('.')) return null;
  return label;
}

/**
 * Folder under public/brand/ for an org's images (hero, logo, headers). Only the
 * launch tenants ship their own set; the platform console and every self-serve
 * community use the Konklave set.
 */
const BRAND_ASSET_DIRS = new Set(['kappa', 'dst']);
export function getBrandAssetDir(orgId: string): string {
  return BRAND_ASSET_DIRS.has(orgId) ? orgId : 'konklave';
}

/**
 * The platform used to live at thecrest.app. Map any legacy thecrest.app URL to
 * its konklave.app equivalent (same subdomain + path), or null if not legacy.
 */
const LEGACY_PLATFORM_HOST = 'thecrest.app';
export function getLegacyPlatformRedirect(loc: Location = window.location): string | null {
  const h = loc.hostname;
  if (h !== LEGACY_PLATFORM_HOST && !h.endsWith(`.${LEGACY_PLATFORM_HOST}`)) return null;
  const host = h.slice(0, -LEGACY_PLATFORM_HOST.length) + PLATFORM_BASE_HOST;
  return `https://${host}${loc.pathname}${loc.search}${loc.hash}`;
}

/**
 * Resolve the organization ID from the current hostname.
 *
 * Resolution order:
 *  1. On localhost, REACT_APP_ORG_ID overrides everything (local tenant testing,
 *     e.g. REACT_APP_ORG_ID=dst npm start).
 *  2. Custom-domain tenants are mapped explicitly via DOMAIN_TO_ORG.
 *  3. Wildcard subdomains — `<org>.konklave.app` resolves to `<org>` so a new
 *     self-serve org works with no code change.
 *  4. Fall back to DEFAULT_ORG_ID.
 */
export function getOrgIdFromHostname(hostname: string = window.location.hostname): string {
  if ((hostname === 'localhost' || hostname === '127.0.0.1') && process.env.REACT_APP_ORG_ID) {
    return process.env.REACT_APP_ORG_ID;
  }
  if (DOMAIN_TO_ORG[hostname]) return DOMAIN_TO_ORG[hostname];
  const sub = getPlatformSubdomainOrg(hostname);
  if (sub) return sub;
  return DEFAULT_ORG_ID;
}

export interface OrganizationBranding {
  primaryColor: string;
  primaryLight: string;
  primaryDark: string;
  secondaryColor: string;
  backgroundColor: string;
  logoUrl: string;
  appIconUrl: string;
}

export interface OrganizationTerminology {
  memberSingular: string;
  memberPlural: string;
  /** Org-type noun, capitalized, e.g. 'Fraternity' / 'Sorority' / 'Organization'. */
  orgNoun: string;
  verifyAction: string;
  inviteAction: string;
  hierarchyTop: string;
  hierarchyMid: string;
  hierarchyBottom: string;
}

export interface OrganizationFeatures {
  marketplace: boolean;
  fundraising: boolean;
  dues: boolean;
  surveys: boolean;
  groups: boolean;
  ticketing: boolean;
  voting: boolean;
  /** Members claim a pre-existing (imported) profile after sign-up. Konklave
   *  only — other tenants register a fresh member, no claim step. */
  profileLinking: boolean;
}

export interface OrganizationBilling {
  /** 'automated' = subject to the trial/paywall; 'manual' = whitelabel/exempt. */
  mode: 'automated' | 'manual';
  /** Current entitlement state (computed server-side). */
  state: 'exempt' | 'trial' | 'active' | 'expired';
  /** What currently grants access. */
  payModel?: 'trial' | 'subscription' | 'license' | null;
  /** Plan key when on a paid plan/license. */
  plan?: string | null;
  /** Access-end epoch ms (period end / license expiry / trial end). */
  until?: number | null;
  /** Trial end (epoch ms), if on trial. */
  trialEndsAt: number | null;
  /** Whole days left until `until` (ceil). */
  daysLeft: number | null;
  /** Recurring subscription set to cancel at period end. */
  cancelAtPeriodEnd?: boolean;
}

export interface OrganizationConfig {
  id: string;
  name: string;
  appName: string;
  domain: string;
  branding: OrganizationBranding;
  terminology: OrganizationTerminology;
  features: OrganizationFeatures;
  billing?: OrganizationBilling;
  /** Super-admin "temporarily unavailable" switch — blocks the app for members. */
  suspended?: boolean;
}

/**
 * Tenant branding comes from Firestore and is not always valid CSS. Some org
 * documents carry values like `rgb108, 0, 0` — the parentheses are missing —
 * which throws inside MUI's `alpha()` / `decomposeColor` and takes down any
 * screen that tints with it.
 *
 * Normalising here means one bad field can no longer break a page: we repair
 * the shapes we recognise and fall back to a known-good colour otherwise.
 */
export function normalizeColor(value: unknown, fallback: string): string {
  if (typeof value !== 'string') return fallback;
  const v = value.trim();
  if (!v) return fallback;

  // Already valid for decomposeColor.
  if (/^#([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(v)) return v;
  if (/^(rgba?|hsla?|color)\(/i.test(v)) return v;

  // Repair the observed malformed form: `rgb108, 0, 0` -> `rgb(108, 0, 0)`.
  const repaired = v.match(/^(rgba?|hsla?)\s*((?:-?[\d.]+%?\s*,\s*){2,3}-?[\d.]+%?)$/i);
  if (repaired) return `${repaired[1].toLowerCase()}(${repaired[2]})`;

  // Bare hex without the hash: `9B0000`.
  if (/^([0-9a-f]{3}|[0-9a-f]{6})$/i.test(v)) return `#${v}`;

  // Named colours are legal CSS but decomposeColor rejects them, so treat
  // anything else as unusable rather than risk a crash.
  return fallback;
}

/** Applies normalizeColor to every colour field, leaving URLs untouched. */
export function normalizeBranding(
  branding: Partial<OrganizationBranding> | undefined,
  fallback: OrganizationBranding,
): OrganizationBranding {
  const b = branding || {};
  return {
    ...fallback,
    ...b,
    primaryColor: normalizeColor(b.primaryColor, fallback.primaryColor),
    primaryLight: normalizeColor(b.primaryLight, fallback.primaryLight),
    primaryDark: normalizeColor(b.primaryDark, fallback.primaryDark),
    secondaryColor: normalizeColor(b.secondaryColor, fallback.secondaryColor),
    backgroundColor: normalizeColor(b.backgroundColor, fallback.backgroundColor),
  };
}

export const FALLBACK_CONFIG: OrganizationConfig = {
  id: 'kappa',
  name: 'Kappa Alpha Psi Fraternity, Inc.',
  appName: 'Konklave',
  domain: 'konklave.app',
  branding: {
    primaryColor: '#A6192E',
    primaryLight: '#C8304A',
    primaryDark: '#7A1222',
    secondaryColor: '#FFD700',
    backgroundColor: '#FFFFFF',
    logoUrl: '',
    appIconUrl: '',
  },
  terminology: {
    memberSingular: 'Brother',
    memberPlural: 'Brothers',
    orgNoun: 'Fraternity',
    verifyAction: 'Verify a Brother',
    inviteAction: 'Invite Brothers',
    hierarchyTop: 'Grand Chapter',
    hierarchyMid: 'Province',
    hierarchyBottom: 'Chapter',
  },
  features: {
    marketplace: true,
    fundraising: true,
    dues: true,
    surveys: true,
    groups: true,
    ticketing: true,
    voting: true,
    profileLinking: true,
  },
};

// Mirrors DST_FALLBACK in the mobile app's src/config/tenant.ts.
const DST_FALLBACK_CONFIG: OrganizationConfig = {
  id: 'dst',
  name: 'Delta Sigma Theta Sorority, Inc. - Far West Region',
  appName: 'CrimsonConnect',
  domain: 'crimsonconnect.app',
  branding: {
    primaryColor: '#9B0000',
    primaryLight: '#C41E3A',
    primaryDark: '#6B0000',
    secondaryColor: '#F5E6CC',
    backgroundColor: '#FFFFFF',
    logoUrl: '/brand/dst/logo.png',
    appIconUrl: '',
  },
  terminology: {
    memberSingular: 'Soror',
    memberPlural: 'Sorors',
    orgNoun: 'Sorority',
    verifyAction: 'Verify a Soror',
    inviteAction: 'Invite Sorors',
    hierarchyTop: 'Grand Chapter',
    hierarchyMid: 'Region',
    hierarchyBottom: 'Chapter',
  },
  features: {
    marketplace: false,
    fundraising: false,
    dues: false,
    surveys: true,
    groups: true,
    ticketing: true,
    voting: true,
    profileLinking: false,
  },
};

/**
 * Launch-tenant seed configs. These exist only so the two original custom-domain
 * tenants render instantly on a cold cache (no branding flash on first visit).
 * NEW organizations do NOT need an entry here: unknown orgs use the generic config
 * below, and their real config loads from Firestore (stale-while-revalidate cache).
 */
const FALLBACK_CONFIGS: Record<string, OrganizationConfig> = {
  kappa: FALLBACK_CONFIG,
  dst: DST_FALLBACK_CONFIG,
};

/**
 * Org-agnostic fallback for any tenant without a seed (i.e. new self-serve orgs).
 * Konklave platform branding until the real config loads from Firestore.
 */
export const GENERIC_FALLBACK_CONFIG: OrganizationConfig = {
  id: 'default',
  name: 'Organization',
  appName: 'Konklave',
  domain: 'konklave.app',
  branding: {
    primaryColor: '#A6192E',
    primaryLight: '#C8304A',
    primaryDark: '#7A1222',
    secondaryColor: '#D4A11E',
    backgroundColor: '#FFFFFF',
    logoUrl: '',
    appIconUrl: '',
  },
  terminology: {
    memberSingular: 'Member',
    memberPlural: 'Members',
    orgNoun: 'Organization',
    verifyAction: 'Verify a Member',
    inviteAction: 'Invite Members',
    hierarchyTop: 'Headquarters',
    hierarchyMid: 'Region',
    hierarchyBottom: 'Chapter',
  },
  features: {
    marketplace: true,
    fundraising: true,
    dues: true,
    surveys: true,
    groups: true,
    ticketing: true,
    voting: true,
    profileLinking: false,
  },
};

/** Konklave platform-console identity (used pre-tenant-selection on admin host). */
export const PLATFORM_CONFIG: OrganizationConfig = {
  ...GENERIC_FALLBACK_CONFIG,
  id: PLATFORM_ORG_SENTINEL,
  name: 'Konklave',
  appName: 'Konklave',
  branding: { ...GENERIC_FALLBACK_CONFIG.branding, logoUrl: '/brand/konklave/logo.png' },
};

/**
 * Offline/first-paint default for an org. Returns the launch-tenant seed when one
 * exists (so kappa/dst never flash the wrong branding), the Konklave platform config
 * for the console sentinel, otherwise the generic config stamped with the orgId.
 */
export function getFallbackConfig(orgId: string): OrganizationConfig {
  if (orgId === PLATFORM_ORG_SENTINEL) return PLATFORM_CONFIG;
  return FALLBACK_CONFIGS[orgId] || { ...GENERIC_FALLBACK_CONFIG, id: orgId };
}

/**
 * Whether the active tenant uses the "claim a pre-existing (imported) profile"
 * flow after sign-up. Kappa does; CrimsonConnect, the Konklave platform and
 * other tenants register a fresh member instead, so they skip the claim step.
 */
export function tenantSupportsProfileClaim(orgId: string = getActiveOrgId()): boolean {
  return getFallbackConfig(orgId).features.profileLinking === true;
}

/**
 * Native app deep-link + app-store info per tenant, used by web landing pages
 * (e.g. the vouch page) to open the correct app and link the correct store.
 *
 * IMPORTANT: only set `scheme` for a tenant whose native app actually registers
 * that custom URL scheme. Konklave (kappa) registers `nupeloop://`. CrimsonConnect
 * (dst) does not register a scheme yet and has no iOS App Store listing, so those
 * are intentionally omitted — otherwise a dst link would open the wrong app.
 */
export interface TenantAppLinks {
  scheme?: string;
  iosAppStoreUrl?: string;
  androidPlayStoreUrl?: string;
}

// The Konklave app (the unified app) serves every community except tenants with
// their own branded app (CrimsonConnect).
const KONKLAVE_APP_LINKS: TenantAppLinks = {
  scheme: 'nupeloop',
  iosAppStoreUrl: 'https://apps.apple.com/us/app/nupe-loop/id6755416369',
  androidPlayStoreUrl: 'https://play.google.com/store/apps/details?id=com.nupeloop',
};

const TENANT_APP_LINKS: Record<string, TenantAppLinks> = {
  kappa: KONKLAVE_APP_LINKS,
  dst: {
    // Scheme registered in ios/CrimsonConnect Info.plist + Android crimsonconnect
    // flavor manifest. No iOS App Store listing yet, so iosAppStoreUrl is omitted.
    scheme: 'crimsonconnect',
    androidPlayStoreUrl: 'https://play.google.com/store/apps/details?id=com.crimsonconnect.app',
  },
};

export function getTenantAppLinks(orgId: string): TenantAppLinks {
  // Every other community lives in the Konklave app.
  return TENANT_APP_LINKS[orgId] || KONKLAVE_APP_LINKS;
}
